import {
    DeleteOutlineRounded,
    ErrorOutlineRounded,
    HelpOutlineRounded,
    InfoRounded,
    NotesRounded,
    ReportProblemOutlined,
} from '@mui/icons-material';
import { ReactNode } from 'react';
import { IntlShape } from 'react-intl';
import Tooltip from '../common/tooltip';

/**
 * Shared, presentational lesson card used by both the cart (`cartLesson`) and the
 * read-only claim/history page (`claimLesson`). Each caller maps its own data shape
 * (LessonCart vs Lesson) into the normalized `LessonCardModel` below, so the visual
 * "refonte complète" markup lives in exactly one place and the two pages stay in sync.
 *
 * Sections render only when their data is present, which lets the read-only claim
 * card omit the price breakdown, delete action, level column, options pricing, etc.
 */

export interface InfoCell {
    icon: ReactNode;
    label: string;
    value: ReactNode;
    badge?: string;
    sub?: string;
}

export interface ParticipantColumn {
    key: string;
    label: string;
}

export interface ParticipantRow {
    initials: string;
    name: string;
    /** Field value per dynamic column key (only present/non-empty fields exist). */
    values: Record<string, string>;
}

export interface OptionRow {
    text: string;
    description?: string;
    required?: boolean;
    qty?: ReactNode;
    unit?: string;
    total?: string;
}

export interface LessonCardModel {
    typePill: string;
    typePillIcon?: ReactNode;
    durationPill?: string;
    title: string;
    price?: { total: string; breakdown?: string; };
    /** Delete handler (cart only). When set, a trash icon button shows in the header. */
    onRemove?: (event: any) => void;
    /** Shown when the line is unavailable or has incomplete participants. */
    problem?: { reason: string; severity?: 'danger' | 'warn'; };
    disabled?: boolean;
    info: InfoCell[];
    participants?: { count: number; total?: number; columns: ParticipantColumn[]; rows: ParticipantRow[]; };
    options?: { rows: OptionRow[]; subtotal?: string; };
    questions?: { q: string; a: string; }[];
    remark?: string;
}

interface Props {
    model: LessonCardModel;
    intl: IntlShape;
}

function LessonCard({ model, intl }: Props) {
    const {
        typePill, typePillIcon, durationPill, title, price, onRemove, problem,
        disabled, info, participants, options, questions, remark,
    } = model;

    const optRows = options?.rows ?? [];
    const showQty = optRows.some((o) => o.qty !== undefined && o.qty !== null);
    const showUnit = optRows.some((o) => o.unit !== undefined && o.unit !== null);
    const showTotal = optRows.some((o) => o.total !== undefined && o.total !== null);
    const optColCount = 1 + (showQty ? 1 : 0) + (showUnit ? 1 : 0) + (showTotal ? 1 : 0);

    return (
        <article className={`__cart-card${disabled ? ' __cart-card-disabled' : ''}`}>
            {problem ? (
                <div className={`__cart-card-reason ${problem.severity === 'danger' ? '__cart-card-reason-danger' : '__cart-card-reason-warn'}`}>
                    {problem.severity === 'danger'
                        ? <ErrorOutlineRounded className="__cart-card-reason-icon" />
                        : <ReportProblemOutlined className="__cart-card-reason-icon" />}
                    <span className="__cart-card-reason-text">{problem.reason}</span>
                    {onRemove ? (
                        <button type="button" className="__cart-card-reason-btn" onClick={onRemove}>
                            {intl.formatMessage({ id: 'cart.removeLesson' })}
                        </button>
                    ) : null}
                </div>
            ) : null}

            <div className="__cart-card-header">
                <div className="__cart-card-head-main">
                    {typePill || durationPill ? (
                        <div className="__cart-pill-row">
                            {typePill ? (
                                <span className="__cart-pill __cart-pill-type">
                                    {typePillIcon}
                                    {typePill}
                                </span>
                            ) : null}
                            {durationPill ? <span className="__cart-pill __cart-pill-muted">{durationPill}</span> : null}
                        </div>
                    ) : null}
                    <h2 className="__cart-card-title">{title}</h2>
                </div>
                <div className="__cart-card-head-aside">
                    {price ? (
                        <div className="__cart-price">
                            <div className="__cart-price-amt">{price.total}</div>
                            {price.breakdown ? <div className="__cart-price-sub">{price.breakdown}</div> : null}
                        </div>
                    ) : null}
                    {onRemove ? (
                        <button
                            type="button"
                            className="__cart-icon-btn"
                            title={intl.formatMessage({ id: 'order.delete' })}
                            onClick={onRemove}
                        >
                            <DeleteOutlineRounded />
                        </button>
                    ) : null}
                </div>
            </div>

            {info.length > 0 ? (
                <div className="__cart-info-grid">
                    {info.map((cell) => (
                        <div className="__cart-info-cell" key={cell.label}>
                            <span className="__cart-info-icon">{cell.icon}</span>
                            <span className="__cart-info-text">
                                <span className="__cart-info-k">
                                    {cell.label}
                                    {
                                        cell.badge ? (
                                            // eslint-disable-next-line react/no-danger
                                            <Tooltip title={<div dangerouslySetInnerHTML={{ __html: cell.badge }} />} PopperProps={{ className: '__cart-info-badge-tooltip' }}>
                                                <InfoRounded className="__cart-info-badge" />
                                            </Tooltip>
                                        ) : null
                                    }
                                </span>
                                <span className="__cart-info-v">
                                    {cell.value}
                                    {cell.sub ? <small>{cell.sub}</small> : null}
                                </span>
                            </span>
                        </div>
                    ))}
                </div>
            ) : null}

            <div className="__cart-panels">
                {participants && participants.rows.length > 0 ? (
                    <div className="__cart-panel">
                        <div className="__cart-panel-head">
                            <span className="__cart-panel-label">{intl.formatMessage({ id: 'booking.Participants' })}</span>
                            <span className="__cart-panel-count">{participants.total ?? participants.count}</span>
                            <span className="__cart-panel-spacer" />
                            {participants.total !== undefined ? (
                                <span className={`__cart-panel-aside${participants.count < participants.total ? ' __cart-panel-aside-warn' : ''}`}>
                                    {intl.formatMessage({ id: 'cart.participantsFilled' }, { filled: participants.count, total: participants.total })}
                                </span>
                            ) : null}
                        </div>
                        <div className="__cart-table-scroll">
                            <table className="__cart-table">
                                <thead>
                                    <tr>
                                        <th>{intl.formatMessage({ id: 'booking.Participant' })}</th>
                                        {participants.columns.map((c) => <th key={c.key}>{c.label}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {participants.rows.map((p, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <tr key={`p_${p.name}_${i}`}>
                                            <td>
                                                <span className="__cart-who">
                                                    <span className="__cart-avatar">{p.initials}</span>
                                                    <span className="__cart-who-name">{p.name}</span>
                                                </span>
                                            </td>
                                            {participants.columns.map((c) => <td key={c.key}>{p.values[c.key] || '-'}</td>)}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : null}

                {optRows.length > 0 ? (
                    <div className="__cart-panel">
                        <div className="__cart-panel-head">
                            <span className="__cart-panel-label">{intl.formatMessage({ id: 'booking.options' })}</span>
                            <span className="__cart-panel-count">{optRows.length}</span>
                        </div>
                        <table className="__cart-table">
                            <thead>
                                <tr>
                                    <th>{intl.formatMessage({ id: 'booking.options' })}</th>
                                    {showQty ? <th className="__cart-num">{intl.formatMessage({ id: 'cart.optionQty' })}</th> : null}
                                    {showUnit ? <th className="__cart-num __cart-num-right">{intl.formatMessage({ id: 'cart.optionUnitPrice' })}</th> : null}
                                    {showTotal ? <th className="__cart-num __cart-num-right">{intl.formatMessage({ id: 'booking.total' })}</th> : null}
                                </tr>
                            </thead>
                            <tbody>
                                {optRows.map((o, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <tr key={`o_${o.text}_${i}`}>
                                        <td>
                                            <div className="__cart-opt-name">
                                                {o.text}
                                                {o.required ? <span className="__cart-opt-required">{intl.formatMessage({ id: 'cart.optionRequired' })}</span> : null}
                                            </div>
                                            {o.description ? <div className="__cart-opt-desc">{o.description}</div> : null}
                                        </td>
                                        {showQty ? <td className="__cart-num">{o.qty ?? '-'}</td> : null}
                                        {showUnit ? <td className="__cart-num">{o.unit ?? '-'}</td> : null}
                                        {showTotal ? <td className="__cart-num">{o.total ?? '-'}</td> : null}
                                    </tr>
                                ))}
                                {options?.subtotal ? (
                                    <tr className="__cart-sub-row">
                                        <td colSpan={optColCount - 1}>{intl.formatMessage({ id: 'cart.optionsSubtotal' })}</td>
                                        <td className="__cart-num">{options.subtotal}</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                ) : null}

                {questions && questions.length > 0 ? (
                    <div className="__cart-panel">
                        <div className="__cart-panel-head">
                            <HelpOutlineRounded className="__cart-panel-ico" />
                            <span className="__cart-panel-label">{intl.formatMessage({ id: 'cart.additionalInfo' })}</span>
                        </div>
                        <div className="__cart-qa">
                            {questions.map((q, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <div className="__cart-qa-item" key={`q_${i}`}>
                                    <div className="__cart-qa-q">{q.q}</div>
                                    <div className="__cart-qa-a">{q.a}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                {remark ? (
                    <div className="__cart-panel">
                        <div className="__cart-remark">
                            <NotesRounded className="__cart-remark-ico" />
                            <div>
                                <div className="__cart-remark-k">{intl.formatMessage({ id: 'booking.remarks' })}</div>
                                <div className="__cart-remark-v">{remark}</div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </article>
    );
}

export default LessonCard;
